import React, { useRef, useState } from "react";
import { EntityType, IExportExcelRequest, IFilterItem } from "../../../model/IExportExcelRequest";
import { Subset } from "../../../typings/GlobalExtensions";
import { Button, Card, CardActions, CardContent, CardHeader, createStyles, Grid, LinearProgress, MenuItem, TextField, Theme, WithStyles, withStyles } from "@material-ui/core";
import PersonalBibNumber from "./Views/PersonalBibNumber";
import { ExportViewRef } from "./Views/models";
import { ApiBackend } from "../../../providers/apibackend";
import MecenatOrders from "./Views/MecenatOrders";
import B2BAdmins from "./Views/B2BAdmins";
import EnteredForRaceWeek from "./Views/EnteredForRaceWeek";
import EventWeekStatistics from "./Views/EventWeekStatistics";
import OrdersUsedCode from "./Views/OrdersUsedCode";
import PreBookedHoodies from "./Views/PreBookedHoodies";
import OnSiteHoodies from "./Views/OnSiteHoodies";
import MedalHemmavasan from "./Views/MedalHemmavasan";
import Orders from "./Views/Orders";

type ExportableTypes = Subset<EntityType, "PersonalBibNumber" | "MecenatOrders" | "B2BAdmins" | "EnteredForRaceWeek" | "EventWeekStatistics" | "PreBookedFinisherHoodies" | "OnSiteFinisherHoodies" | "MedalHemmavasan" | "Orders" | "OrdersUsedCode">;
type EmptyValueType = "EMPTY_VALUE";
const EMPTY_VALUE: EmptyValueType = "EMPTY_VALUE";

// Exporttyper i dropdownen, sorterade på namn.
const exportTypeOptions = ([
    { value: "PersonalBibNumber", label: "Personlig nummerlapp" },
    { value: "MecenatOrders", label: "Mecenat beställningar" },
    { value: "B2BAdmins", label: "B2B Företagsadministratörer" },
    { value: "EnteredForRaceWeek", label: "Anmälda till evenemangsvecka" },
    { value: "EventWeekStatistics", label: "Statistik Evenemangsvecka" },
    { value: "PreBookedFinisherHoodies", label: "Förbokade Finisher-hoodies för evenemangsvecka" },
    { value: "OnSiteFinisherHoodies", label: "Uthämtade hoodies/medaljgraveringar (förköpt + på plats) för evenemangsvecka" },
    { value: "MedalHemmavasan", label: "Export Medalj Hemmavasan" },
    { value: "Orders", label: "Orders av produkt" },
    { value: "OrdersUsedCode", label: "Orders som nyttjat kod" },
] as { value: ExportableTypes; label: string }[]).sort((a, b) => a.label.localeCompare(b.label, "sv"));

const ExportOrders = ({ classes }: WithStyles) => {
    const [exportType, setExportType] = useState(EMPTY_VALUE as ExportableTypes | EmptyValueType);
    const [filter, setFilter] = useState(null as IFilterItem[]);
    const [loading, setLoading] = useState(false);

    const ref = useRef<ExportViewRef>(null);
    const api = new ApiBackend();

    const getFilterView = () => {
        switch (exportType) {
            case "PersonalBibNumber":
                return <PersonalBibNumber ref={ref} onChange={setFilter} />
            case "MecenatOrders":
                return <MecenatOrders ref={ref} onChange={setFilter} />
            case "B2BAdmins":
                return <B2BAdmins ref={ref} onChange={setFilter} />
            case "EnteredForRaceWeek":
                return <EnteredForRaceWeek ref={ref} onChange={setFilter} />
            case "EventWeekStatistics":
                return <EventWeekStatistics ref={ref} onChange={setFilter} />
            case "OrdersUsedCode":
                return <OrdersUsedCode ref={ref} onChange={setFilter} />
            case "PreBookedFinisherHoodies":
                return <PreBookedHoodies ref={ref} onChange={setFilter} />
            case "OnSiteFinisherHoodies":
                return <OnSiteHoodies ref={ref} onChange={setFilter} />
            case "MedalHemmavasan":
                return <MedalHemmavasan ref={ref} onChange={setFilter} />
            case "Orders":
                return <Orders ref={ref} onChange={setFilter} />
            default:
                return null;
        }
    };

    const exportExcel = async () => {
        const request: IExportExcelRequest = {
            entityType: exportType as ExportableTypes,
            filter: filter
        };
        try {
            setLoading(true);

            await api.exportExcel(request);
        } finally {
            setLoading(false);
        }
    };

    const isExportEnabled = () => {
        return filter && ref.current?.isValid();
    };

    const render = () => {
        return (<Card>

            <CardHeader className={classes.cardHeader} title="Exporter" />
            <CardContent>
                {loading &&
                    <LinearProgress color="secondary" />
                }
                <Grid container className={classes.root} spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            select
                            style={{ flex: 1, margin: '0 3px' }}
                            label="Exporttyp"
                            InputLabelProps={{ shrink: true }}
                            value={exportType ?? ""}
                            fullWidth
                            onChange={(e) => {
                                setExportType(e.target.value as ExportableTypes);
                            }}
                        >
                            {exportType === EMPTY_VALUE && <MenuItem key={EMPTY_VALUE} value={EMPTY_VALUE}>VÄLJ</MenuItem>}
                            {exportTypeOptions.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12}>
                        {getFilterView()}
                    </Grid>
                </Grid>
            </CardContent>
            <CardActions>
                <Button size="small" color="secondary"
                    disabled={!isExportEnabled()}
                    onClick={exportExcel}
                >
                    Exportera
                </Button>
            </CardActions>
        </Card>
        );
    };

    return render();
};

const useStyles = ({ palette, spacing }: Theme) => createStyles({
    cardHeader: {
        background: palette.secondary.main,
        color: palette.secondary.contrastText,
        padding: 3
    },
    photo: {
        height: "30px",
        verticalAlign: "middle",
        borderRadius: "10px"
    },
    root: {

    },
    form: {
        "& > *": {
            margin: spacing(1),
            width: "25ch",
        },
        "& label.Mui-focused": {
            color: palette.secondary.main,
        },
        "& .MuiInput-underline:after": {
            borderBottomColor: palette.secondary.main,
        },
    }
});

export default withStyles(useStyles)(ExportOrders);
